import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import Loading from './Loading';
import { useObjectMemoDeep } from '@client/hooks/use-object.hooks';
type LoaderData = { text?: 'Signing in' | 'Loading Nodon' | 'Loading project' };

type GlobalLoaderContext = {
  current: LoaderData | undefined;
  add: (props: LoaderData) => { remove: () => void };
};
const NO_PROVIDER_ERROR =
  'No GlobalLoader.Provider rendered. GlobalLoader will not work.';
const defaultContext: GlobalLoaderContext = {
  get current() {
    console.error(NO_PROVIDER_ERROR);
    return undefined;
  },
  get add() {
    console.error(NO_PROVIDER_ERROR);
    return () => ({ remove: () => undefined });
  },
};

const LoaderQueueContext = createContext<GlobalLoaderContext>(defaultContext);

const GlobalLoaderProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<{ data: LoaderData; id: string }[]>([]);

  const current = useMemo(() => queue.at(0)?.data, [queue]);
  const add = useCallback((data: LoaderData) => {
    const id = uuidv4();
    setQueue((prev) => [...prev, { data, id }]);
    const remove = () => setQueue((prev) => prev.filter((p) => p.id !== id));
    return { remove };
  }, []);
  const ctxValue = useObjectMemoDeep({ current, add });
  return (
    <LoaderQueueContext.Provider value={ctxValue}>
      {children}
    </LoaderQueueContext.Provider>
  );
};

/**
 * Render the component to register a loader to be shown by the GlobalQueueLoader
 * @param param0
 * @returns
 */
const RegisterLoader: FC<{ open?: boolean } & LoaderData> = ({
  text = 'Loading Nodon',
  open = true,
}) => {
  const { add } = useContext(LoaderQueueContext);
  useEffect(() => (open ? add({ text }).remove : undefined), [text, open, add]);
  return null;
};

/**
 * Render the component to show the global loader
 * Will displayed if there's any loader registered in the queue
 * @returns
 */
const GlobalLoaderComponent: FC = () => {
  const { current } = useContext(LoaderQueueContext);
  return (
    <Loading
      open={current !== undefined}
      text={current?.text}
      variant="dots"
      surface
    />
  );
};

export const GlobalLoader = Object.assign(GlobalLoaderComponent, {
  Provider: GlobalLoaderProvider,
  Register: RegisterLoader,
});
